import clsx from 'clsx';
import Image from 'next/image';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useMedia } from 'react-use';

import { api } from '@endaoment-frontend/api';
import { OTCAddress } from '@endaoment-frontend/types';
import { CopyIcon, EthereumIcon } from '@endaoment-frontend/ui/icons';
import { getShortAddress } from '@endaoment-frontend/utils';

import { CardTable } from '../../cardTable';
import { TypeformButton } from '../typeformButton';

import styles from './OTCSearch.module.scss';

const OTCToken = (props: OTCAddress) => {
  const { address, symbol, name, logoURI, memo } = props;
  const [showDonate, setShowDonate] = useState(false);

  const isMobile = useMedia('(max-width: 40rem)', true);

  return (
    <CardTable.Row
      className={styles['otc-token']}
      onMouseOver={() => {
        setShowDonate(true);
      }}
      onMouseLeave={() => {
        setShowDonate(false);
      }}
    >
      <CardTable.Col>
        <div>
          {logoURI ? (
            <Image src={logoURI} alt={name} width={38} height={38} />
          ) : (
            <EthereumIcon width={38} height={38} />
          )}
          <span>
            {name}
            <b>{symbol && ` (${symbol.toUpperCase()})`}</b>
          </span>
          <TypeformButton
            className={clsx(styles['donate-button'], showDonate && styles['visible'])}
            otcAddress={props}
            small
          >
            Donate
          </TypeformButton>
        </div>
      </CardTable.Col>
      <CardTable.Col>
        <div className={clsx('flex-row', styles['copyable-column'])}>
          <span>{isMobile ? '' : getShortAddress(address)}</span>
          <CopyIcon
            onClick={() => {
              navigator.clipboard.writeText(memo);
            }}
          />
        </div>
      </CardTable.Col>
      <CardTable.Col>
        <div className={clsx('flex-row', styles['copyable-column'])}>
          <span>{isMobile ? '' : getShortAddress(address)}</span>
          <CopyIcon
            onClick={() => {
              navigator.clipboard.writeText(address);
            }}
          />
        </div>
      </CardTable.Col>
    </CardTable.Row>
  );
};

export const OTCSearch = () => {
  const { data: otcAddressesData } = useQuery(api.GetOTCAddresses.key, api.GetOTCAddresses.query);

  // TODO: Move searching to backend
  const [search, setSearch] = useState('');
  const otcAddresses = useMemo(() => {
    if (otcAddressesData) {
      return otcAddressesData.filter((address) => address.name.includes(search));
    }
    return [];
  }, [search, otcAddressesData]);

  return (
    <section className={styles['otc-search-container']}>
      <div className={styles['otc-search']}>
        <input
          placeholder="Search addresses, ticker, or token name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <TypeformButton>Start OTC Donation</TypeformButton>
      </div>
      <CardTable headers={['Token', 'Memo*', 'Address']}>
        {otcAddresses.map((address, i) => (
          <OTCToken key={i + address.name} {...address} />
        ))}
      </CardTable>
      <p className={styles['otc-footnote']}>
        *Memo is required for the transaction if it is included in the table for that token type.
        <br />
        Questions? Reach out to{' '}
        <a href="mailto:otc@endaoment.org" target="_blank" rel="noreferrer">
          otc@endaoment.org
        </a>{' '}
        for assistance.
      </p>
    </section>
  );
};
